/**
 * `SaleSlice` — WEB. Own `createSlice` over `sharedReducers` plus web's two reducers, its
 * divergent `saveSaleData`/`resetSaleStore`, and the logout `extraReducers` mobile has no
 * equivalent of.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  SaleDataType,
  ChannelTypes,
  SaleStatusTypes,
  TransactionType,
  ServiceLabel,
} from "../../../models/sale";
import { CHANNEL_TYPES } from "../../../models/sale";
import { supportLogout, userLogout } from "../LoginSlice";
import { sharedInitialState, sharedReducers, type SharedSaleState } from "./shared";

interface Props extends SharedSaleState {
  saleData?: SaleDataType,
  saleValidated?: boolean,
}

/**
 * The starting shape of a sale.
 *
 * 🔴 **It lives HERE, in the slice, and not in `helpers/saleHelpers`, on purpose (2026-08-27).**
 * `initialState` below calls it at module-EVAL time. While it lived in `saleHelpers` — a file the
 * slice imported and which imports the store back — that call ran across a cycle, and if the
 * bundler reached `saleHelpers` first it executed BEFORE that file's own body statements. It cost
 * the apps a boot-time `TypeError: ObjectID is not a function` twice (hazard 34), and audit check
 * H flagged the same shape again the moment core's state readers went back to importing the store
 * directly. Moving the function to the module that calls it removes the cross-module eval-time
 * call outright — the fix the ledger has called "option C" since session 33. `saleHelpers`
 * re-exports it, so every existing importer is unchanged.
 *
 * `bson-objectid` is still required INSIDE the function: as a module-scope `var` it does not
 * hoist. `require` is cached, so the repeat calls from `resetSaleStore`/logout are free.
 */
export function getBasicSaleData(isKiosk = false): SaleDataType {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const ObjectID = require("bson-objectid");
  return ({
    "mid": ObjectID().toHexString(),
    "branchId": "",
    /**
     * `KIOSK` for a kiosk sale, `POS` otherwise. The CALLER decides (`isKiosk`) — this function
     * must not read the kiosk flag itself: `initialState` below calls it at module-EVAL time,
     * and reaching `kiosk/config` (and the store it reads) from that path is the hazard this
     * file's header is about. `helpers/saleHelpers.resetSaleData` passes it.
     */
    "channel": isKiosk ? CHANNEL_TYPES.KIOSK : CHANNEL_TYPES.POS as ChannelTypes,
    "currency": "",
    "saleItems": [],
    "itemCount": 0,
    "saleStatus": "Open" as SaleStatusTypes,
    "billSubTotal": 0,
    "discountTotal": 0,
    "billTotal": 0,
    "billRoundedAmount": 0,
    "discountList": [],
    "billDiscountList": [],
    "chargeTotal": 0,
    "chargeList": [],
    "transactionType": "Sale" as TransactionType,
    "channelId": "",
    "channelName": "",
    "channelDefId": "",
    "groupId": "",
    "createTickets": false,
    "createdAt": "",
    "lastItemNumber": undefined,
    "lastTicketNumber": undefined,
    "serviceLabel": {} as ServiceLabel,
    "itemDiscountList": [],
    "saleBy": undefined,
  });
}

const initialState: Props = {
  ...sharedInitialState,
  saleData: getBasicSaleData(),
  saleValidated: false,
}

export const saleSlice = createSlice({
  name: "saleData",
  initialState,
  reducers: {
    ...sharedReducers,
    // ⚠️ Divergent — see the table in shared.ts. This body splits `saleValidated` off the
    // payload and stores it separately, and stamps `isHoldOrder` when converting Held -> Open.
    saveSaleData: (state, action: PayloadAction<any>) => {
      const { saleValidated, ...saleData } = action.payload || {};
      if (saleData?.saleStatus === "Held") {
        saleData.saleStatus = "Open";
        saleData['isHoldOrder'] = true;
        console.log('Converted to Open');
      }
      state.saleValidated = !!saleValidated;
      state.saleData = saleData;
    },
    // ⚠️ Divergent — see the table in shared.ts.
    resetSaleStore: (state) => {
      state.saleData = getBasicSaleData()
      state.guestInfo = {}
      state.selectedChannelData = {}
      state.isHoldSaleCall = true
    },
    updateSaleSlice: (state, action: PayloadAction<any>) => {
      return action.payload;
    },
    reopenOrder: (state, action: PayloadAction<any>) => { },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userLogout, (state) => {
        state.saleData = getBasicSaleData();
        state.guestInfo = {};
        state.selectedChannelData = {};
        state.isHoldSaleCall = true;
        state.updateSelectedChannelDataOffline = false;
      })
      .addCase(supportLogout, (state) => {
        state.saleData = getBasicSaleData();
        state.guestInfo = {};
        state.selectedChannelData = {};
        state.isHoldSaleCall = true;
        state.updateSelectedChannelDataOffline = false;
      });
  },
});

export const {
  saveSaleData,
  saveSelectedChannel,
  saveGuestInfo,
  holdSaleData,
  resetSaleStore,
  saveSelectedChannelOffline,
  updateSaleSlice,
  reopenOrder,
} = saleSlice.actions

export default saleSlice.reducer;
